import axios from 'axios'
import Settings from 'app/config/appConfig'

import { ROUTES } from 'app/constants/routes'
import contentDisposition from 'content-disposition'
import { dispatchCompanyErrorModal, getValidAccessToken } from '@/lib/axios'

const timeout: number = 300000
const errorTimeout: number = 300000

const STATUS_CODES_FOR_INTERNAL_HANDLING = [401, 500, 504]
export const serviceURL: string = `${Settings.serverAddress}`

export const downloadFile = async (
  url: string,
  data: any,
  handleErrorInternally: boolean = true,
  mimeType: string = 'application/pdf',
  saveToDisk: boolean = true,
): Promise<any> => {
  const token = await getValidAccessToken()
  return new Promise((resolve, reject) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    const instance = axios.create({
      baseURL: serviceURL,
      timeout: 5 * 60 * 1000,
      cancelToken: source.token,
      responseType: 'arraybuffer',
    })

    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    })
    instance
      .post(url, data)
      .then((response) => {
        const headerCt =
          response.headers['content-type'] ||
          response.headers['Content-Type']
        const effectiveType = headerCt
          ? String(headerCt).split(';')[0].trim()
          : mimeType
        if (saveToDisk) {
          const file = new Blob([response.data], { type: effectiveType })
          const filename = resolveDownloadFileName(response, effectiveType)
          const fileURL = URL.createObjectURL(file)
          openFile(fileURL, filename)
        }

        resolve({ status: response.status, content: response })
      })
      .catch((error) => {
        if (error.response && error.response.status == 400) {
          let code = 'EMPTY_DOCUMENT'
          let details: string | null = null
          try {
            const raw = error.response.data
            const text =
              typeof raw === 'string'
                ? raw
                : new TextDecoder('utf-8').decode(new Uint8Array(raw))
            const parsed = JSON.parse(text)
            if (parsed?.code) code = parsed.code
            if (parsed?.details) details = parsed.details
          } catch {
            /* keep defaults */
          }
          reject({ status: 400, code, details })
          return
        }
        handleServiceError(error, handleErrorInternally, reject)
      })
  })
}
export const fetchBlobForPreview = async (
  url: string,
  data: any,
  mimeType: string = 'application/pdf',
): Promise<{ blobUrl: string; filename: string; mimeType: string }> => {
  const token = await getValidAccessToken()
  const instance = axios.create({
    baseURL: serviceURL,
    timeout: 5 * 60 * 1000,
    responseType: 'arraybuffer',
  })
  instance.interceptors.request.use((config) => {
    if (token) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` }
    }
    return config
  })
  const response = await instance.post(url, data)
  const headerCt =
    response.headers['content-type'] || response.headers['Content-Type']
  const effectiveType = headerCt
    ? String(headerCt).split(';')[0].trim()
    : mimeType
  const blob = new Blob([response.data], { type: effectiveType })
  const blobUrl = URL.createObjectURL(blob)
  const filename = resolveDownloadFileName(response, effectiveType)
  return { blobUrl, filename, mimeType: effectiveType }
}

export const get = async (
  url: string,
  handleErrorInternally: boolean = true,
  avoidServiceCallsNotification: boolean = false,
): Promise<any> => {
  const token = await getValidAccessToken()
  return new Promise((resolve, reject) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    let isFulfilled = false

    const instance = axios.create({
      baseURL: serviceURL,
      timeout: timeout,
      cancelToken: source.token,
    })
    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    })
    instance
      .get(url)
      .then((response) => {
        isFulfilled = true
        // if (!avoidServiceCallsNotification) {
        //    dispatch && dispatch(serviceCallEnded())
        // }

        resolve({ status: response.status, content: response })
      })
      .catch((error) => {
        isFulfilled = true
        // if (!avoidServiceCallsNotification) {
        //    dispatch && dispatch(serviceCallEnded())
        // }

        handleServiceError(error, handleErrorInternally, reject)
      })

    setTimeout(() => {
      if (!isFulfilled) {
        source.cancel('NETWORK_ERROR')
      }
    }, errorTimeout)
  })
}

export const post = async (
  url: string,
  data: any,
  handleErrorInternally: boolean = true,
  contentType: string = 'application/json',
  avoidServiceCallsNotification: boolean = false,
  forceHandleAllErrors: boolean = false,
): Promise<any> => {
  const token = await getValidAccessToken()
  return new Promise((resolve, reject) => {
    const CancelToken = axios.CancelToken
    const source = CancelToken.source()

    let isFulfilled = false
    // if (!avoidServiceCallsNotification) {
    //   dispatch(serviceCallStarted())
    // }

    const instance = axios.create({
      baseURL: serviceURL,
      timeout: timeout,
      cancelToken: source.token,
      headers: {
        'Content-type': contentType,
      },
    })
    instance.interceptors.request.use((config) => {
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        }
      }
      return config
    })

    instance
      .post(url, data)
      .then((response) => {
        isFulfilled = true
        // if (!avoidServiceCallsNotification) {
        //   dispatch(serviceCallEnded())
        // }

        resolve({ status: response.status, content: response })
      })
      .catch((error) => {
        isFulfilled = true
        // if (!avoidServiceCallsNotification) {
        //   dispatch(serviceCallEnded())
        // }

        handleServiceError(
          error,
          handleErrorInternally,
          //   dispatch,
          reject,
          null,
          null,
          forceHandleAllErrors,
        )
      })

    setTimeout(() => {
      if (!isFulfilled) {
        source.cancel('NETWORK_ERROR')
      }
    }, errorTimeout)
  })
}

const determineErrorPageUrl = () => {
  const index = window.location.pathname.indexOf('/company/')
  if (index !== -1) {
    let companyId = window.location.pathname.substring(index + 9)
    companyId = companyId.substring(0, companyId.indexOf('/'))
    return ROUTES.GenericErrorWithCompany.replace(':companyId', companyId)
  }
  return ROUTES.GenericError
}

const handleServiceError = (
  error: any,
  handleErrorInternally: boolean,
  //   dispatch: Dispatch,
  reject: (reason?: any) => void,
  msg: string = null,
  code: string = null,
  forceHandleAllErrors: boolean = false,
) => {
  const message = msg != null ? msg : prepareErrorMessage(error)

  if (error.code === 'ECONNABORTED') {
    // dispatch(serviceCallError('0', 'errorCodes.general-error', message, true))
    return
  } else if (error.response == null) {
    // dispatch(serviceCallError('0', 'errorCodes.general-error', message, true))
    return
  }

  if (
    !forceHandleAllErrors &&
    (handleErrorInternally ||
      STATUS_CODES_FOR_INTERNAL_HANDLING.find(
        (s) => s == error.response.status,
      ))
  ) {
    if (window.location.pathname.includes('/error')) {
      return
    }

    if (error.response.data && error.response.data.ErrorCode === 'NO_CLAIM') {
      //   redirectToLogin(dispatch)
      return
    }

    // Obsłużone modalem przez GlobalErrorHandler — nie wyrzucaj na stronę błędu.
    // Ta warstwa nie przechodzi przez interceptor `@/lib/axios`, więc zdarzenie
    // trzeba wysłać samemu; bez tego modal by się nie pokazał.
    //
    // NOT_MIGRATED_COMPANY nie robiło tu wcześniej nic - cała gałąź była zakomentowana,
    // więc wydruki firmy w trakcie migracji kończyły się pustym ekranem bez komunikatu.
    const companyErrorCode = error.response.data?.ErrorCode
    if (
      companyErrorCode === 'COMPANY_HAS_NO_DATABASE' ||
      companyErrorCode === 'NOT_MIGRATED_COMPANY'
    ) {
      dispatchCompanyErrorModal(companyErrorCode, error)
      return
    }

    switch (error.response.status) {
      case 401:
        // redirectToLogin(dispatch)
        break
      case 404:
        const url = determineErrorPageUrl()
        // dispatch(
        //   showError({
        //     description: message,
        //     statusCode: error.response.status,
        //   }),
        // )
        // dispatch(push(url))
        break
      case 500:
      case 504:
        // dispatch(
        //   serviceCallError(
        //     error.response.status,
        //     'errorCodes.general-error',
        //     message,
        //     true,
        //   ),
        // )
        break
      case 403:
        // dispatch(
        //   showMessage({
        //     text: 'errorCodes.forbidden',
        //     details: message,
        //     type: MessageType.ERROR,
        //     translate: true,
        //   }),
        // )
        break
      case 400:
        if (typeof code === 'string' && code.startsWith('EMPTY_DOCUMENT')) {
          // Show as warning, not error
          console.warn(`Empty document generated (${code})`)
        }
        // dispatch(
        //   serviceCallError(
        //     error.response.status,
        //     message,
        //     `errorCode.${code != null ? code : error.response.code}`,
        //     true,
        //   ),
        // )
        break
      default:
      // dispatch(showMessage({ text: message, type: MessageType.ERROR }))
    }
  } else {
    reject({
      status: axios.isCancel(error)
        ? 'exception.service-unavailable'
        : error.response,
    })
  }
}

const prepareErrorMessage = (error: any) => {
  let message = error.message

  if (error.code === 'ECONNABORTED') {
    message = 'exception.timeout'
  } else if (error.response == null) {
    message = 'exception.no-internet-connection'
  } else {
    switch (error.response.status) {
      case 401:
        message = 'exception.not-authorized'
        break
      case 403:
        message = 'exception.forbidden'
        break
      case 404:
        message = 'exception.not-found'
        break
      case 500:
        message = 'exception.unexpected-exception'
        break
      case 504:
        message = 'exception.service-unavailable'
        break
    }
  }
  return message
}

const openFile = (url, name) => {
  const link = document.createElement('a')
  link.download = name
  link.href = url
  link.setAttribute('target', '_blank')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}


const FALLBACK_EXTENSION_BY_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'application/zip': 'zip',
  'text/csv': 'csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
}

const fallbackFileName = (mimeType?: string) =>
  `attachment.${FALLBACK_EXTENSION_BY_MIME[mimeType ?? ''] ?? 'bin'}`

const resolveDownloadFileName = (response: any, mimeType?: string): string => {
  const header =
    response.headers['content-disposition'] ||
    response.headers['Content-Disposition']
  if (header) {
    try {
      const parsed = contentDisposition.parse(header)
      const fn = parsed.parameters.filename
      if (fn != null && String(fn).length > 0) {
        return String(fn)
      }
    } catch {
      /* fallback below */
    }
  }
  return extractFileNameLegacy(response, mimeType)
}

const extractFileNameLegacy = (response: any, mimeType?: string): string => {
  const header =
    response.headers['content-disposition'] ||
    response.headers['Content-Disposition']
  if (!header) {
    return fallbackFileName(mimeType)
  }
  const parts = header.split('filename=')
  if (parts.length < 2) {
    return fallbackFileName(mimeType)
  }
  const filename = parts[1]
    .split(';')[0]
    .replace(/^["']|["']$/g, '')
    .trim()
  if (!filename) {
    return fallbackFileName(mimeType)
  }
  if (/^[A-Za-z0-9+/=]+$/.test(filename)) {
    try {
      return base64DecodeUnicode(filename)
    } catch {
      return filename
    }
  }
  return filename
}

const base64DecodeUnicode = (str) => {
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join(''),
  )
}
